import {
  Box,
  Checkbox,
  Container,
  Flex,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  type ElementType,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FaFolderOpen } from "react-icons/fa";
import { StorageService } from "@/client/sdk.gen";
import type { FileDetailWithStatus } from "@/client/types.gen";
import {
  FileList,
  type FileItem,
  convertToFileItem,
} from "@/components/Document/FileList";
import useCustomToast from "@/hooks/useCustomToast";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { z } from "zod";

import AddFolder from "@/components/Document/AddFolder";

interface Folder {
  id: string;
  prefix_name: string;
  description: string;
  user_id: string;
  created_at: string;
  updated_at: string;
}

interface DisplayableItem {
  id: string; // folder.id
  name: string; // folder.prefix_name
  keyForSelection: string; // folder.id
  type: "folder";
  originalData: Folder;
  icon: ElementType; // Icon component
}

interface DeleteFilesResponse {
  message: string;
  deleted_document_ids: string[];
  warning?: string;
  opensearch_errors?: string[];
}

interface PresignedUrlResponse {
  url: string;
}

const chatsSearchSchema = z.object({
  page: z.number().catch(1),
});

export const Route = createFileRoute("/_layout/documentList")({
  component: DocumentList,
  validateSearch: (search) => chatsSearchSchema.parse(search),
});

function DocumentList() {
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const { showSuccessToast, showErrorToast } = useCustomToast();

  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [hoveredFileId, setHoveredFileId] = useState<string | null>(null);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const [hoveredFolderId, setHoveredFolderId] = useState<string | null>(null);
  const [isFolderSelectionMode, setIsFolderSelectionMode] = useState(false);
  const [isAddFolderOpen, setIsAddFolderOpen] = useState(false);
  const [, setSelected] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("ALL"); // "전체" 선택을 명시적으로 표현
  const [dateFilter, setDateFilter] = useState<Date | null>(null);

  const { page } = Route.useSearch();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [, setActiveFolderId] = useState<string | null>(null);

  // 상태 필터 변경 핸들러 - 페이지 리셋 포함
  const handleStatusFilterChange = (status: string) => {
    console.log("🔄 상태 필터 변경 핸들러 호출:", status);
    setStatusFilter(status);
    // 상태 필터 변경 시 페이지를 1로 리셋
    navigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        page: 1,
      }),
    });
  };

  // 날짜 필터 변경 핸들러 - 페이지 리셋 포함
  const handleDateFilterChange = (date: Date | null) => {
    console.log("📅 날짜 필터 변경 핸들러 호출:", date);
    setDateFilter(date);
    // 날짜 필터 변경 시 페이지를 1로 리셋
    navigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        page: 1,
      }),
    });
  };

  const { data: foldersData } = useQuery({
    queryKey: ["foldersList"],
    queryFn: async () => {
      try {
        const response = await StorageService.listFolders();
        return {
          data: response as Folder[],
          count: (response as Folder[]).length,
        };
      } catch (error) {
        return { data: [], count: 0 };
      }
    },
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: true,
  });

  const folders: Folder[] = foldersData?.data ?? [];

  // 폴더 목록이 로드될 때 폴더 선택 처리 - 최적화된 버전
  useEffect(() => {
    const savedFolderId = localStorage.getItem("selectedFolderId");

    if (folders.length > 0) {
      let targetFolderId: string | null = null;

      if (savedFolderId) {
        // 저장된 폴더 ID가 있는 경우 해당 폴더 선택
        const savedFolder = folders.find(
          (folder: Folder) => folder.id === savedFolderId
        );
        if (savedFolder) {
          targetFolderId = savedFolder.id;
        } else {
          // 저장된 폴더가 없는 경우 첫 번째 폴더 선택
          targetFolderId = folders[0].id;
          localStorage.setItem("selectedFolderId", targetFolderId);
        }
      } else {
        // 저장된 폴더 ID가 없는 경우 첫 번째 폴더 선택
        targetFolderId = folders[0].id;
        localStorage.setItem("selectedFolderId", targetFolderId);
      }

      // 현재 선택된 폴더와 다른 경우에만 상태 업데이트
      if (targetFolderId && targetFolderId !== selectedFolderId) {
        setSelected(targetFolderId);
        setSelectedFolderId(targetFolderId);
        setActiveFolderId(targetFolderId);
      }
    }
  }, [folders, selectedFolderId]); // selectedFolderId 의존성 추가

  const displayItems = useMemo((): DisplayableItem[] => {
    const items = folders.map(
      (f: Folder): DisplayableItem => ({
        id: f.id,
        name: f.prefix_name,
        keyForSelection: f.id,
        type: "folder" as const,
        originalData: f,
        icon: FaFolderOpen,
      })
    );
    return items.sort((a: DisplayableItem, b: DisplayableItem) =>
      a.name.localeCompare(b.name, "ko-KR", {
        sensitivity: "base",
        numeric: true,
        caseFirst: "upper",
      })
    );
  }, [folders]);

  const { data: filesData, refetch } = useQuery({
    queryKey: ["filesWithStatus", selectedFolderId],
    queryFn: async (): Promise<FileDetailWithStatus[]> => {
      if (!selectedFolderId) {
        return [];
      }
      try {
        const response = await StorageService.listFilesWithStatusInFolder({
          folderId: selectedFolderId,
        });
        return response.data || [];
      } catch (error) {
        showErrorToast("파일 및 상태 목록을 가져오는데 실패했습니다.");
        return [];
      }
    },
    enabled: !!selectedFolderId && selectedFolderId.length > 0,
    staleTime: 1000 * 30, // 30초 (처리 중인 파일이 없을 때)
    gcTime: 1000 * 60 * 5, // 5분 캐시 유지
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false, // 네트워크 재연결 시에도 자동 리페치 비활성화
    retry: 1, // 재시도 횟수 제한
    retryDelay: 1000, // 재시도 간격
  });

  const files: FileItem[] = useMemo(() => {
    if (!filesData) return [];
    return filesData.map(convertToFileItem);
  }, [filesData]);

  // 상태 변경 감지를 위한 useEffect 추가 - 최적화된 버전
  useEffect(() => {
    if (files.length > 0) {
      const hasProcessingFiles = files.some(
        (file: FileItem) =>
          file.status === "PROCESSING" || file.status === "PENDING"
      );

      let intervalId: NodeJS.Timeout;

      if (hasProcessingFiles) {
        // 처리 중인 파일이 있으면 5초마다 갱신 (기존 3초에서 5초로 증가)
        intervalId = setInterval(() => {
          // 현재 폴더가 선택된 상태에서만 리페치
          if (selectedFolderId) {
            refetch();
          }
        }, 5000);
      }

      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }
  }, [files, refetch, selectedFolderId]);

  const handleItemClick = (item: DisplayableItem) => {
    setActiveFolderId(item.id);
    setSelectedFolderId(item.keyForSelection);
    localStorage.setItem("selectedFolderId", item.keyForSelection);
    setSelectedFiles([]);
    setIsSelectionMode(false);
    navigate({
      search: (prev: Record<string, unknown>) => ({ ...prev, page: 1 }),
    });
  };

  const handleDeleteFolders = async () => {
    if (selectedFolders.length === 0) return;
    if (!window.confirm("선택한 폴더를 삭제하시겠습니까?")) return;

    try {
      // 유효한 폴더 ID만 필터링
      const validFolderIds = selectedFolders.filter((folderId: string) => {
        const folder = folders.find((f: Folder) => f.id === folderId);
        return folder !== undefined && folder.id !== undefined;
      });

      if (validFolderIds.length === 0) {
        showErrorToast("삭제할 수 있는 폴더가 없습니다.");
        return;
      }

      // 삭제할 폴더 중 현재 선택된 폴더가 있는지 확인
      const isCurrentFolderBeingDeleted = validFolderIds.includes(
        selectedFolderId || ""
      );

      for (const folderId of validFolderIds) {
        await StorageService.deleteFolder({ folderId });
      }

      // 폴더 목록 즉시 갱신
      await queryClient.invalidateQueries({ queryKey: ["foldersList"] });

      // 현재 선택된 폴더가 삭제된 경우, 파일 목록 쿼리도 무효화
      if (isCurrentFolderBeingDeleted) {
        await queryClient.invalidateQueries({
          queryKey: ["filesWithStatus", selectedFolderId],
        });
      }

      setSelectedFolders([]);
      setIsFolderSelectionMode(false);

      showSuccessToast("선택한 폴더가 삭제되었습니다.");
    } catch (e) {
      console.error("폴더 삭제 중 오류:", e);
      showErrorToast("폴더 삭제 중 오류가 발생했습니다.");
    }
  };

  const handleToggleFolderSelection = (
    folderId: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation(); // 폴더 클릭 이벤트 전파 방지
    setSelectedFolders((prev: string[]) => {
      const newSelected = prev.includes(folderId)
        ? prev.filter((id: string) => id !== folderId)
        : [...prev, folderId];

      // 선택된 폴더가 없으면 선택 모드 해제
      if (newSelected.length === 0) {
        setIsFolderSelectionMode(false);
      } else if (!isFolderSelectionMode) {
        setIsFolderSelectionMode(true);
      }

      return newSelected;
    });
  };

  const handleDeleteFiles = async () => {
    if (!selectedFolderId) {
      showErrorToast("파일을 삭제할 폴더를 먼저 선택해주세요.");
      return;
    }
    if (selectedFiles.length === 0) return;
    if (!window.confirm("정말 삭제하시겠습니까?")) return;

    try {
      // selectedFiles는 이제 document_id 배열이므로 직접 사용
      const documentIds = selectedFiles.filter(
        (id): id is string => id !== undefined && id !== null
      );

      if (documentIds.length === 0) {
        showErrorToast("삭제할 파일의 문서 정보를 찾을 수 없습니다.");
        return;
      }

      const response = (await StorageService.deleteFiles({
        requestBody: {
          document_ids: documentIds,
        },
      })) as DeleteFilesResponse;

      // 파일 목록 즉시 갱신
      await queryClient.invalidateQueries({
        queryKey: ["filesWithStatus", selectedFolderId],
      });
      setSelectedFiles([]);
      setIsSelectionMode(false);

      // 응답에 경고 메시지가 있는 경우 표시
      if (response.warning) {
        showErrorToast(response.warning);
      } else {
        showSuccessToast(response.message || "삭제가 완료되었습니다.");
      }
    } catch (error) {
      // API 에러 메시지가 있는 경우 사용
      const message =
        error instanceof Error ? error.message : "삭제 중 오류가 발생했습니다.";
      showErrorToast(message);
    }
  };

  const handleRefresh = () => {
    if (selectedFolderId) {
      queryClient.invalidateQueries({
        queryKey: ["filesWithStatus", selectedFolderId],
      });
    }
  };

  // 다중 파일 선택을 위한 새로운 핸들러
  const handleFileSelection = useCallback(
    (documentId: string) => {
      if (!isSelectionMode) {
        setIsSelectionMode(true);
      }

      if (documentId.includes(",")) {
        const ids = documentId.split(",");
        setSelectedFiles((prev) => {
          const allSelected = ids.every((id) => prev.includes(id));
          if (allSelected) {
            return prev.filter((id) => !ids.includes(id));
          }
          return [...prev, ...ids.filter((id) => !prev.includes(id))];
        });
        return;
      }

      setSelectedFiles((prev: string[]) =>
        prev.includes(documentId)
          ? prev.filter((id: string) => id !== documentId)
          : [...prev, documentId]
      );
    },
    [isSelectionMode]
  );

  // 다운로드 핸들러
  const handleDownload = useCallback(
    async (objectName: string) => {
      try {
        const response = (await StorageService.getPresignedUrl({
          objectName,
        })) as PresignedUrlResponse;
        window.open(response.url, "_blank");
      } catch (e) {
        showErrorToast("다운로드 URL 생성에 실패했습니다.");
      }
    },
    [showErrorToast]
  );

  const handleFileHover = (fileId?: string) => {
    setHoveredFileId(fileId ?? null);
  };

  return (
    <Container maxW="full" px={4}>
      <Box mt={5}>
        <Text fontSize="2xl" fontWeight="bold" mb={6}>
          문서 관리
        </Text>

        <Flex
          gap={10}
          px={8}
          align="center"
          width="100%"
          justifyContent="flex-start"
          position="sticky"
          top={0}
          bg="white"
          zIndex={10}
          py={4}
          borderBottom="1px"
          borderColor="gray.100"
        >
          {displayItems.map((item) => (
            <Box
              key={item.id}
              position="relative"
              onMouseEnter={() => setHoveredFolderId(item.id)}
              onMouseLeave={() => setHoveredFolderId(null)}
            >
              <VStack
                gap={2}
                cursor="pointer"
                onClick={() => handleItemClick(item)}
                position="relative"
                p={2}
                borderRadius="md"
                _hover={{ bg: "gray.50" }}
              >
                {(hoveredFolderId === item.id ||
                  selectedFolders.includes(item.id)) && (
                  <Box
                    position="absolute"
                    top={2}
                    right={2}
                    zIndex={1}
                    onClick={(e) => handleToggleFolderSelection(item.id, e)}
                  >
                    <Checkbox.Root
                      checked={selectedFolders.includes(item.id)}
                      size="sm"
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control
                        border="1px solid"
                        borderColor="blue.500"
                        _checked={{ bg: "blue.500" }}
                      >
                        <Checkbox.Indicator />
                      </Checkbox.Control>
                    </Checkbox.Root>
                  </Box>
                )}
                <Box>
                  <Icon
                    as={FaFolderOpen}
                    boxSize={6}
                    color={
                      selectedFolderId === item.id ? "blue.500" : "gray.500"
                    }
                  />
                </Box>
                <Text
                  fontWeight={selectedFolderId === item.id ? "bold" : "normal"}
                  color={selectedFolderId === item.id ? "blue.500" : "gray.600"}
                  fontSize="md"
                >
                  {item.name}
                </Text>
              </VStack>
            </Box>
          ))}
          {isFolderSelectionMode ? (
            <VStack
              gap={2}
              cursor="pointer"
              onClick={handleDeleteFolders}
              opacity={selectedFolders.length === 0 ? 0.5 : 1}
              pointerEvents={selectedFolders.length === 0 ? "none" : "auto"}
              minW="80px"
              alignItems="center"
            >
              <Box
                bg="gray.100"
                borderRadius="full"
                p={1.5}
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="40px"
                h="40px"
              >
                <Icon
                  as={FiTrash2}
                  boxSize={5}
                  color={selectedFolders.length > 0 ? "red.500" : "gray.500"}
                />
              </Box>
              <Text
                color={selectedFolders.length > 0 ? "red.500" : "gray.400"}
                fontSize="md"
                textAlign="center"
                w="100%"
              >
                삭제
              </Text>
            </VStack>
          ) : (
            <VStack
              gap={2}
              cursor="pointer"
              onClick={() => setIsAddFolderOpen(true)}
              minW="80px"
              alignItems="center"
            >
              <Box
                bg="gray.100"
                borderRadius="full"
                p={1.5}
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="40px"
                h="40px"
              >
                <Icon as={FiPlus} boxSize={5} color="gray.500" />
              </Box>
              <Text color="gray.400" fontSize="md" textAlign="center" w="100%">
                새 폴더
              </Text>
            </VStack>
          )}
          <AddFolder
            isOpen={isAddFolderOpen}
            setIsOpen={setIsAddFolderOpen}
            onAddFolder={async (folderName: string, description: string) => {
              try {
                // 폴더 생성 요청
                const response = await StorageService.createFolder({
                  requestBody: {
                    folder_name: folderName,
                    prefix_name: folderName,
                    description: description,
                  },
                });

                // 폴더 목록 즉시 갱신
                await queryClient.invalidateQueries({
                  queryKey: ["foldersList"],
                });

                // 생성된 폴더의 ID를 직접 사용 (응답에서 가져오거나 예상)
                // API 응답에서 폴더 ID를 가져오는 것이 더 안전합니다
                const responseData = response as { id?: string };
                if (responseData && typeof responseData.id === "string") {
                  // 새로 생성된 폴더를 선택
                  setSelected(responseData.id);
                  setSelectedFolderId(responseData.id);
                  setActiveFolderId(responseData.id);
                  localStorage.setItem("selectedFolderId", responseData.id);
                  showSuccessToast("폴더가 생성되었습니다.");
                } else {
                  // 응답에서 ID를 가져올 수 없는 경우, 폴더 목록을 다시 조회
                  const updatedFolders =
                    (await StorageService.listFolders()) as Folder[];
                  if (updatedFolders && updatedFolders.length > 0) {
                    // 새로 생성된 폴더 찾기
                    const newFolder = updatedFolders.find(
                      (folder: Folder) => folder.prefix_name === folderName
                    );

                    if (newFolder) {
                      // 새로 생성된 폴더를 선택
                      setSelected(newFolder.id);
                      setSelectedFolderId(newFolder.id);
                      setActiveFolderId(newFolder.id);
                      localStorage.setItem("selectedFolderId", newFolder.id);
                      showSuccessToast("폴더가 생성되었습니다.");
                    } else {
                      // 새로 생성된 폴더를 찾지 못한 경우 첫 번째 폴더 선택
                      const firstFolder = updatedFolders[0];
                      setSelected(firstFolder.id);
                      setSelectedFolderId(firstFolder.id);
                      setActiveFolderId(firstFolder.id);
                      localStorage.setItem("selectedFolderId", firstFolder.id);
                      showSuccessToast("폴더가 생성되었습니다.");
                    }
                  }
                }
              } catch (e) {
                console.error("폴더 생성 에러:", e);
                showErrorToast("폴더 생성에 실패했습니다.");
              }
            }}
          />
        </Flex>

        <FileList
          selectedFolderId={selectedFolderId ?? undefined}
          fileState={{
            files,
            selectedFiles,
            hoveredFileId: hoveredFileId ?? undefined,
            onFileSelect: handleFileSelection,
            onFileHover: handleFileHover,
            onDeleteFiles: handleDeleteFiles,
            onDownload: handleDownload,
          }}
          searchState={{
            searchQuery,
            onSearchChange: setSearchQuery,
          }}
          filterState={{
            statusFilter,
            onStatusFilterChange: handleStatusFilterChange,
            dateFilter,
            onDateFilterChange: handleDateFilterChange,
          }}
          pageState={{
            currentPage: page,
            onPageChange: ({ page: newPage }) =>
              navigate({
                search: (prev: Record<string, unknown>) => ({
                  ...prev,
                  page: newPage,
                }),
              }),
            onRefresh: handleRefresh,
          }}
          folders={folders.map((f) => ({ ...f, folder_name: f.prefix_name }))}
        />
      </Box>
    </Container>
  );
}
